import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiLogOut } from "react-icons/fi";
import { HiOutlineMenuAlt3 } from "react-icons/hi";
import { MoonIcon, SunIcon } from "@heroicons/react/24/outline"; // ✅ เพิ่ม icon
import Swal from "sweetalert2";
import { clearQuotationCache } from "../utils/quotationCache";
import { COMPANY_FINANCE_NAME } from "../config/config";
import { clearTimesheetCache } from "../utils/timesheetCache";
import NotificationBell from "./NotificationBell";

const Header = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const [isDarkMode, setIsDarkMode] = useState(false); // ✅ state theme

  // ✅ โหลด theme จาก localStorage
  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const dark = storedTheme === "dark" || (!storedTheme && prefersDark);
    setIsDarkMode(dark);
    document.documentElement.classList.toggle("dark", dark);
  }, []);

  // ✅ ฟังก์ชันสลับ Theme
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    localStorage.setItem("theme", newTheme ? "dark" : "light");
    document.documentElement.classList.toggle("dark", newTheme);

    // 🔥 แจ้งให้ทุก component ในระบบรู้ว่า theme เปลี่ยนแล้ว
    window.dispatchEvent(new Event("theme-change"));
  };

  // ✅ Logout function
  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Logout?",
      text: "คุณต้องการออกจากระบบหรือไม่",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "ออกจากระบบ",
      cancelButtonText: "ยกเลิก",
      reverseButtons: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
    });

    if (result.isConfirmed) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("teams");
      localStorage.removeItem("departments");
      localStorage.removeItem("timesheetClients");
      clearQuotationCache();
      clearTimesheetCache();

      await Swal.fire({
        title: "ออกจากระบบสำเร็จ",
        text: "คุณได้ออกจากระบบแล้ว",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/login");
    }
  };

  return (
    <header
      className="
        sticky top-0 z-40 w-full 
        bg-white/60 dark:bg-gray-900 
        backdrop-blur-md 
        border-b border-gray-200 dark:border-gray-800 
        shadow-sm 
        px-4 sm:px-6 py-4 
        transition-colors duration-500
      "
    >
      <div className="flex items-center justify-between">
        {/* Left: Hamburger + Logo */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="md:hidden text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white p-1 rounded-md"
          >
            <HiOutlineMenuAlt3 size={24} />
          </button>
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-white tracking-wide pl-10 md:pl-0">
            {COMPANY_FINANCE_NAME} 💡
          </h1>
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          {/* 🔔 Notification Bell (render ครั้งเดียวทั้ง desktop/mobile กัน poll ซ้ำ) */}
          {user && <NotificationBell />}

          {/* Right: Desktop */}
          <div className="hidden md:flex items-center gap-6 relative">
            {/* 🌙 Theme toggle */}
            <button
              onClick={toggleTheme}
              className="
                text-gray-700 
                dark:text-yellow-400 
                hover:text-indigo-600 
                dark:hover:text-yellow-300 
                transition-colors duration-300
                p-2 rounded-full
                focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:focus:ring-yellow-400
              "
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? (
                <SunIcon className="w-6 h-6 drop-shadow-sm" />
              ) : (
                <MoonIcon className="w-6 h-6 drop-shadow-sm" />
              )}
            </button>

            {/* Hello User + Logout */}
            {user && (
              <span className="text-gray-600 dark:text-gray-300 font-medium">
                👋 Hello, {user.firstName}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md shadow transition"
            >
              <FiLogOut size={18} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>

          {/* Right: Mobile */}
          <div className="md:hidden">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md shadow transition"
            >
              <FiLogOut size={18} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
